import { useCallback, useEffect, useState } from "react";
import { adminListTeam, adminAddTeamMember, adminChangeTeamRole, adminRemoveTeamMember } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { ShieldCheck, UserPlus, Trash2, Loader2, Lock } from "lucide-react";

const ROLE_STYLES = {
  Admin: "bg-ccc-plum text-white",
  Staff: "bg-ccc-gold/20 text-ccc-plum border border-ccc-gold/50",
};

const SOURCE_LABELS = {
  env: { label: "Environment", hint: "Set in server configuration — locked" },
  invite: { label: "Invited", hint: "Granted from this page" },
  direct: { label: "Direct", hint: "Role set directly on the account" },
};

function fmtRelative(iso) {
  if (!iso) return "—";
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return iso; }
}

export default function Team() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("Staff");
  const [saving, setSaving] = useState(false);
  const [busyEmail, setBusyEmail] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await adminListTeam();
      setData(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load team access");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addMember = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSaving(true);
    try {
      await adminAddTeamMember(email.trim(), role);
      toast.success(`${email.trim()} can now sign in as ${role}`);
      setEmail("");
      setRole("Staff");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not grant access");
    } finally { setSaving(false); }
  };

  const changeRole = async (m, newRole) => {
    if (newRole === m.role) return;
    setBusyEmail(m.email);
    try {
      await adminChangeTeamRole(m.email, newRole);
      toast.success(`${m.email} is now ${newRole}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change role");
    } finally { setBusyEmail(null); }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove CRM access for ${m.email}? Their active sessions will be signed out.`)) return;
    setBusyEmail(m.email);
    try {
      await adminRemoveTeamMember(m.email);
      toast.success(`Access removed for ${m.email}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove access");
    } finally { setBusyEmail(null); }
  };

  const loginUrl = `${window.location.origin}/admin/login`;

  return (
    <div data-testid="admin-team-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-wider text-ccc-purple">Operations</div>
        <h1 className="font-serif text-4xl text-ccc-plum">Team Access.</h1>
        <p className="text-sm text-stone-600 mt-1">
          Who may sign in to the back office, and with which role.
        </p>
      </div>

      <div className="border border-ccc-gold/40 bg-ccc-gold/5 p-5 mb-8 max-w-3xl" data-testid="team-how-it-works">
        <div className="font-serif text-lg text-ccc-plum flex items-center gap-2 mb-1">
          <ShieldCheck size={18} className="text-ccc-gold"/> How colleagues get in
        </div>
        <ol className="text-sm text-stone-700 list-decimal ml-5 space-y-1">
          <li>Add their email address below and choose a role.</li>
          <li>Share the sign-in link: <span className="font-mono text-xs bg-white border border-stone-200 px-1.5 py-0.5 select-all" data-testid="team-login-url">{loginUrl}</span></li>
          <li>They sign in with the Google account for that email — no password to manage.</li>
        </ol>
        <p className="text-xs text-stone-500 mt-2">
          <strong>Staff</strong> can work in every module. <strong>Admin</strong> additionally controls team access, budgets, and destructive operations.
        </p>
      </div>

      <form onSubmit={addMember} className="flex flex-wrap items-end gap-3 mb-8 max-w-3xl" data-testid="team-add-form">
        <div className="flex-1 min-w-[240px]">
          <label className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Email address</label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="colleague@archden.org"
            data-testid="team-email-input"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Role</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="h-9 border border-stone-300 bg-white px-3 text-sm"
            data-testid="team-role-select"
          >
            <option value="Staff">Staff</option>
            <option value="Admin">Admin</option>
          </select>
        </div>
        <Button type="submit" disabled={saving || !email.trim()}
                className="bg-ccc-purple hover:bg-ccc-plum text-white"
                data-testid="team-add-btn">
          {saving ? <Loader2 size={14} className="mr-1 animate-spin"/> : <UserPlus size={14} className="mr-1"/>}
          Grant access
        </Button>
      </form>

      {loading ? (
        <div className="text-sm text-stone-500">Loading…</div>
      ) : (
        <div className="border border-stone-200 bg-white max-w-4xl" data-testid="team-members-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-stone-200">
                <th className="px-4 py-2.5">Member</th>
                <th className="px-4 py-2.5">Role</th>
                <th className="px-4 py-2.5">Source</th>
                <th className="px-4 py-2.5">Last sign-in</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.members || []).map(m => {
                const locked = m.source === "env";
                const isYou = m.email === data?.you;
                const busy = busyEmail === m.email;
                const src = SOURCE_LABELS[m.source] || SOURCE_LABELS.direct;
                return (
                  <tr key={m.email} className="border-b border-stone-100 last:border-0" data-testid={`team-row-${m.email}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-ccc-plum">{m.email}{isYou && <span className="text-xs text-stone-400 ml-1">(you)</span>}</div>
                      {m.name && <div className="text-xs text-stone-500">{m.name}</div>}
                      {!m.has_signed_in && <div className="text-xs text-stone-400 italic">Hasn't signed in yet</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${ROLE_STYLES[m.role] || "bg-stone-100 text-stone-600"}`}>
                        {m.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-stone-600 flex items-center gap-1" title={src.hint}>
                        {locked && <Lock size={11} className="text-stone-400"/>} {src.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-stone-600 text-xs">{fmtRelative(m.last_login_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {!locked && !isYou && (
                          <>
                            <select
                              value={m.role}
                              disabled={busy}
                              onChange={(e) => changeRole(m, e.target.value)}
                              className="h-7 border border-stone-300 bg-white px-1.5 text-xs"
                              data-testid={`team-role-change-${m.email}`}
                            >
                              <option value="Staff">Staff</option>
                              <option value="Admin">Admin</option>
                            </select>
                            <Button size="sm" variant="outline" disabled={busy}
                                    onClick={() => removeMember(m)}
                                    className="h-7 px-2 text-red-700 border-red-200 hover:bg-red-50"
                                    data-testid={`team-remove-${m.email}`}>
                              {busy ? <Loader2 size={12} className="animate-spin"/> : <Trash2 size={12}/>}
                            </Button>
                          </>
                        )}
                        {locked && <span className="text-[10px] uppercase tracking-wider text-stone-400">Locked</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {(data?.members || []).length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-stone-500 text-sm">No team members yet. Grant access above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
